import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

const getTitlePostFix = ({ titlePostfix, companyName }) => {
  if (titlePostfix === null) {
    return `%s | ${companyName}`;
  }
  if (!titlePostfix) {
    return '%s';
  }

  return `%s | ${titlePostfix}`;
};

const PageHelmet = ({
  title,
  titlePostfix,
  companyName,
  description,
  canonical,
  noIndex,
  metaTags,
  slug,
  alternativeLocales,
  hostname,
  disableLangTags,
}) => {
  const intl = useIntl();

  const helmetProps = {
    title,
    titleTemplate: getTitlePostFix({ titlePostfix, companyName }),
    link: [],
  };

  const helmetMeta = [];

  if (description) {
    helmetMeta.push({
      name: 'description',
      content: description,
    });
  }


  if (noIndex) {
    helmetMeta.push({
      name: 'robots',
      content: 'noindex',
    });
  }

  if (metaTags) {
    metaTags.forEach((tag) => {
      if (tag.content && tag.content.length) helmetMeta.push(tag);
    });
  }

  if (canonical) {
    helmetProps.link = [{
      rel: 'canonical',
      href: canonical,
    }];
  } else {
    helmetProps.link = [{
      rel: 'canonical',
      href: slug,
    }];
  }

  if (helmetMeta.length > 0) { helmetProps.meta = helmetMeta; }
  if (!disableLangTags && alternativeLocales && alternativeLocales.length) {
    alternativeLocales.forEach(({ locale: pageLocale, path: pagePath }) => {
      helmetProps.link.push({
        rel: 'alternate',
        href: `${hostname}${pagePath}`,
        hreflang: pageLocale,
      });
    });
  }

  return (
    <Helmet {...helmetProps}>
      <html lang={intl.locale} />
    </Helmet>
  );
};

PageHelmet.propTypes = {
  title: PropTypes.string.isRequired,
  titlePostfix: PropTypes.string,
  companyName: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  noIndex: PropTypes.bool,
  slug: PropTypes.string,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }),
  metaTags: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.string.isRequired,
    property: PropTypes.string.isRequired,
  })),
  facilityId: PropTypes.string,
  facility: PropTypes.object,
  support: PropTypes.shape({
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  socialMedia: PropTypes.arrayOf(PropTypes.shape({
    provider: PropTypes.string,
    enabled: PropTypes.bool,
    link: PropTypes.string,
  })),
  disableLangTags: PropTypes.bool,
  alternativeLocales: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    locale: PropTypes.string,
    path: PropTypes.string,
  })),
  hostname: PropTypes.string,
};

PageHelmet.defaultProps = {
  disableLangTags: false,
};

export default PageHelmet;
